
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { documentStore, uiStore } from "@/store/store";
import domToImage from "dom-to-image-more";
import { pipe } from "fp-ts/lib/function";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { fold } from "fp-ts/lib/Either";
import NotificationLogger from "@/lib/notifications/notifications";
import WPMediaInput from "@/components/Reusable/CustomInputs/MediaInput/WPMediaInput.vue";
import { Media } from "@/types/media";
import { Screenshot, ScreenshotV } from "@/types/design-library";
import { placeholderImageDataUrl } from "@/util/placeholderImage";
import MediaUploadDialog from "@/components/Reusable/CustomInputs/MediaInput/MediaUploadDialog.vue";
import { forceNextTick } from "@/util/force-next-tick";

@Component({
  components: { MediaUploadDialog, WPMediaInput }
})
export default class ElementScreenshot extends Vue {
  @Prop()
  value!: Screenshot | undefined;

  @Prop()
  controlType!: "page" | "section";

  loading = false;

  openMediaModal = false;

  takeScreenshot() {
    if (!documentStore.document.tree.root.children.length) {
      NotificationLogger.log.message("There are no elements added to the page");

      return;
    }

    const isPage = this.controlType === "page";
    const querySelector = isPage
      ? "#breakdance_canvas"
      : `[data-node-id="${uiStore.activeElementId}"]`;

    const activeSectionDom =
      window.Breakdance.canvas.document.querySelector(querySelector);

    const activeElement = uiStore.activeElement;

    // This should never happen
    if (!activeSectionDom || (!isPage && !activeElement)) {
      NotificationLogger.log.message("Unexpected error occurred.");
      return;
    }

    let elementName: string | null = null;

    if (isPage) {
      elementName = `${documentStore.document.documentMeta.titleLabel}_${documentStore.document.documentMeta.id}`;
    } else if (activeElement) {
      const elementNameFromSettings =
        activeElement.data.properties?.settings?.library?.name;

      elementName = elementNameFromSettings
        ? `${elementNameFromSettings}_${activeElement.id}`
        : null;
    }

    if (!elementName) {
      NotificationLogger.log.message(
        "The field 'name' is required to create an automatic screenshot"
      );

      return;
    }

    // remove all non alphanumeric chars to prevent the file name from being invalid
    const cleanElementName = elementName.replace(/[^a-zA-Z0-9 -]/g, "");
    const prefixedElementName = `${
      isPage ? "design_page_" : "design_section_"
    }${cleanElementName}`;

    this.loading = true;

    // Deselect the active element, otherwise the NodeTitlebar will show in the screenshot
    const activeElementPlaceholder = uiStore.activeElementId;
    uiStore.activateElement(null);

    // Wait for the browser to remove the node titlebar
    forceNextTick(() => {
      domToImage
        .toBlob(activeSectionDom, {
          imagePlaceholder: placeholderImageDataUrl,
          bgcolor: "#FFF"
        })
        .then(async blob => {
          pipe(
            await makeWordPressAjaxRequest({
              action: "breakdance_upload_img_to_media_library",
              dataToPost: {
                image: blob,
                name: prefixedElementName,
                id: this.value?.id
              },
              decoder: ScreenshotV,
              resultKey: "data"
            }),
            fold(
              errors => {
                this.loading = false;
                errors.forEach(e => NotificationLogger.log.message(e.msg));

                // Re-activate the element
                uiStore.activateElement(activeElementPlaceholder);
              },
              screenshot => {
                this.loading = false;
                this.$emit("input", screenshot);

                // Re-activate the element
                uiStore.activateElement(activeElementPlaceholder);
              }
            )
          );
        })
        .catch(() => {
          this.loading = false;

          NotificationLogger.log.message(
            "Screenshot couldn't be created, probably because it links to protected third party images. Please add an image manually"
          );
        });
    });
  }

  handleWpMedia(newValue: Media) {
    this.$emit("input", { url: newValue.url, id: newValue.id });
  }
}
